import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import BrandMark from '../brand/BrandMark';

const NAV = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/mission', label: 'Mission & Vision' },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: 'Contact' },
];

export default function SiteHeader() {
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
                scrolled
                    ? 'bg-background/70 backdrop-blur-xl border-b border-white/10'
                    : 'bg-transparent border-b border-transparent'
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
                <Link to="/" className="flex items-center gap-2 group" data-testid="site-logo">
                    <BrandMark className="h-7 w-7" />
                    <span className="font-display text-base sm:text-lg font-semibold tracking-tight">
                        DigiTech <span className="text-primary">Cloud AI</span>
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-1" data-testid="site-navigation">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-link-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `relative px-3 py-2 text-sm rounded-md transition-colors ${
                                    isActive
                                        ? 'text-primary'
                                        : 'text-foreground/75 hover:text-foreground hover:bg-white/5'
                                }`
                            }
                        >
                            {item.label}
                            {pathname === item.to && (
                                <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-primary/70" />
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-2">
                    <Button
                        asChild
                        className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_0_1px_rgba(0,229,255,0.35),0_0_24px_rgba(0,229,255,0.18)]"
                        data-testid="nav-book-consultation-button"
                    >
                        <Link to="/contact" className="inline-flex items-center gap-1.5">
                            Book a Consultation
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </Button>
                </div>

                {/* Mobile */}
                <div className="md:hidden">
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid="mobile-nav-open-button" className="text-foreground">
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-background border-white/10 text-foreground w-[88%] sm:w-96">
                            <div className="flex items-center justify-between mb-8">
                                <Link to="/" className="flex items-center gap-2">
                                    <BrandMark className="h-6 w-6" />
                                    <span className="font-display font-semibold">DigiTech Cloud AI</span>
                                </Link>
                                <SheetClose asChild>
                                    <Button variant="ghost" size="icon" data-testid="mobile-nav-close-button">
                                        <X className="h-5 w-5" />
                                    </Button>
                                </SheetClose>
                            </div>
                            <div className="flex flex-col gap-1">
                                {NAV.map((item) => (
                                    <SheetClose asChild key={item.to}>
                                        <NavLink
                                            to={item.to}
                                            data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                                            className={({ isActive }) =>
                                                `px-3 py-3 rounded-md text-base ${isActive ? 'text-primary bg-white/5' : 'text-foreground/85 hover:bg-white/5'}`
                                            }
                                        >
                                            {item.label}
                                        </NavLink>
                                    </SheetClose>
                                ))}
                                <SheetClose asChild>
                                    <Link
                                        to="/contact"
                                        data-testid="mobile-nav-book-consultation-button"
                                        className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-3 font-medium"
                                    >
                                        Book a Consultation
                                        <ArrowUpRight className="h-4 w-4" />
                                    </Link>
                                </SheetClose>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
